const EARTH_RADIUS_M = 6371008.8;
const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;

export interface LatLng {
  latitude: number;
  longitude: number;
}

/** [latitude, longitude] tuple */
export type LatLngTuple = [number, number];

/** [longitude, latitude] tuple (GeoJSON order) */
export type LngLatTuple = [number, number];

export interface GeoJSONPoint {
  type: 'Point';
  coordinates: [number, number];
}

export interface GeoJSONLineString {
  type: 'LineString';
  coordinates: LngLatTuple[];
}

export interface GeoJSONPolygon {
  type: 'Polygon';
  coordinates: LngLatTuple[][];
}

export interface GeoJSONFeature<G extends GeoJSONLineString | GeoJSONPolygon = GeoJSONLineString | GeoJSONPolygon> {
  type: 'Feature';
  properties: Record<string, unknown>;
  geometry: G;
}

export interface GeoJSONFeatureCollection<G extends GeoJSONLineString | GeoJSONPolygon = GeoJSONLineString | GeoJSONPolygon> {
  type: 'FeatureCollection';
  features: GeoJSONFeature<G>[];
}

function stripClosingPosition(positions: LatLngTuple[]): LatLngTuple[] {
  if (positions.length < 2) return positions;

  const [firstLat, firstLng] = positions[0];
  const [lastLat, lastLng] = positions[positions.length - 1];
  if (firstLat === lastLat && firstLng === lastLng) {
    return positions.slice(0, -1);
  }

  return positions;
}

/**
 * Parse a PostGIS EWKB hex string into { latitude, longitude }.
 * Returns null if the input is invalid or unparseable.
 */
export function parseEwkbPoint(hex: string): LatLng | null {
  if (!hex || hex.length < 42) return null;
  try {
    const isLittleEndian = hex.substring(0, 2) === '01';

    function readDouble(offset: number): number {
      const bytes: number[] = [];
      for (let i = 0; i < 8; i += 1) {
        bytes.push(parseInt(hex.substring(offset + i * 2, offset + i * 2 + 2), 16));
      }
      if (!isLittleEndian) bytes.reverse();
      const buffer = new ArrayBuffer(8);
      const view = new DataView(buffer);
      bytes.forEach((byte, index) => view.setUint8(index, byte));
      return view.getFloat64(0, true);
    }

    let offset = 2;
    const typeHex = hex.substring(offset, offset + 8);
    offset += 8;

    const typeBytes: number[] = [];
    for (let i = 0; i < 4; i += 1) {
      typeBytes.push(parseInt(typeHex.substring(i * 2, i * 2 + 2), 16));
    }
    const typeBuffer = new ArrayBuffer(4);
    const typeView = new DataView(typeBuffer);
    typeBytes.forEach((byte, index) => typeView.setUint8(index, byte));
    const typeValue = typeView.getUint32(0, isLittleEndian);

    if ((typeValue & 0x20000000) !== 0) {
      offset += 8;
    }

    const lng = readDouble(offset);
    offset += 16;
    const lat = readDouble(offset);

    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { latitude: lat, longitude: lng };
  } catch {
    return null;
  }
}

export function isValidLatLng(position: unknown): position is LatLngTuple {
  return Array.isArray(position)
    && position.length >= 2
    && Number.isFinite(position[0])
    && Number.isFinite(position[1]);
}

export function parseGeometryPoint(raw: unknown): LatLngTuple | null {
  if (!raw) return null;

  if (isValidLatLng(raw)) {
    return [Number(raw[0]), Number(raw[1])];
  }

  if (typeof raw === 'object' && raw !== null) {
    const obj = raw as Record<string, unknown>;

    if (Array.isArray(obj['coordinates']) && (obj['coordinates'] as unknown[]).length >= 2) {
      const [lng, lat] = obj['coordinates'] as [unknown, unknown];
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return [Number(lat), Number(lng)];
      }
    }

    if (Number.isFinite(obj['latitude']) && Number.isFinite(obj['longitude'])) {
      return [Number(obj['latitude']), Number(obj['longitude'])];
    }
  }

  if (typeof raw === 'string') {
    const wktMatch = raw.match(/POINT\s*\(\s*([-\d.]+)\s+([-\d.]+)\s*\)/i);
    if (wktMatch) {
      return [parseFloat(wktMatch[2]), parseFloat(wktMatch[1])];
    }

    const ewkb = parseEwkbPoint(raw);
    if (ewkb) {
      return [ewkb.latitude, ewkb.longitude];
    }
  }

  return null;
}

export function toLngLat(position: LatLngTuple): LngLatTuple | null {
  if (!isValidLatLng(position)) return null;
  return [position[1], position[0]];
}

export function getBoundsFromPositions(positions: LatLngTuple[]): [LngLatTuple, LngLatTuple] | null {
  const valid = positions.map(toLngLat).filter((p): p is LngLatTuple => p !== null);
  if (!valid.length) return null;

  let minLng = valid[0][0];
  let minLat = valid[0][1];
  let maxLng = valid[0][0];
  let maxLat = valid[0][1];

  valid.forEach(([lng, lat]) => {
    minLng = Math.min(minLng, lng);
    minLat = Math.min(minLat, lat);
    maxLng = Math.max(maxLng, lng);
    maxLat = Math.max(maxLat, lat);
  });

  return [[minLng, minLat], [maxLng, maxLat]];
}

export function createFeatureCollection<G extends GeoJSONLineString | GeoJSONPolygon>(
  features: (GeoJSONFeature<G> | null | undefined)[],
): GeoJSONFeatureCollection<G> {
  return {
    type: 'FeatureCollection',
    features: features.filter((f): f is GeoJSONFeature<G> => f != null),
  };
}

export function createLineFeature(
  positions: LatLngTuple[],
  properties: Record<string, unknown> = {},
): GeoJSONFeature<GeoJSONLineString> | null {
  const coordinates = positions.map(toLngLat).filter((p): p is LngLatTuple => p !== null);
  if (coordinates.length < 2) return null;

  return {
    type: 'Feature',
    properties,
    geometry: {
      type: 'LineString',
      coordinates,
    },
  };
}

export function createPolygonFeature(
  positions: LatLngTuple[],
  properties: Record<string, unknown> = {},
): GeoJSONFeature<GeoJSONPolygon> | null {
  const normalized = stripClosingPosition(positions);
  if (normalized.length < 3) return null;

  const coordinates = normalized
    .map(toLngLat)
    .filter((position): position is LngLatTuple => position !== null);

  if (coordinates.length < 3) return null;

  const [firstLng, firstLat] = coordinates[0];
  const [lastLng, lastLat] = coordinates[coordinates.length - 1];
  if (firstLng !== lastLng || firstLat !== lastLat) {
    coordinates.push([firstLng, firstLat]);
  }

  return {
    type: 'Feature',
    properties,
    geometry: {
      type: 'Polygon',
      coordinates: [coordinates],
    },
  };
}

export function createCircleFeature(
  center: LatLngTuple,
  radiusMeters: number,
  steps = 64,
  properties: Record<string, unknown> = {},
): GeoJSONFeature<GeoJSONPolygon> | null {
  const lngLat = toLngLat(center);
  if (!lngLat || !Number.isFinite(radiusMeters) || radiusMeters <= 0) return null;

  const [lng, lat] = lngLat;
  const angularDistance = radiusMeters / EARTH_RADIUS_M;
  const latRad = lat * DEG_TO_RAD;
  const lngRad = lng * DEG_TO_RAD;
  const coordinates: LngLatTuple[] = [];

  for (let step = 0; step <= steps; step += 1) {
    const bearing = (step / steps) * Math.PI * 2;
    const sinLat = Math.sin(latRad);
    const cosLat = Math.cos(latRad);
    const sinDistance = Math.sin(angularDistance);
    const cosDistance = Math.cos(angularDistance);

    const circleLat = Math.asin(
      sinLat * cosDistance + cosLat * sinDistance * Math.cos(bearing),
    );
    const circleLng = lngRad + Math.atan2(
      Math.sin(bearing) * sinDistance * cosLat,
      cosDistance - sinLat * Math.sin(circleLat),
    );

    coordinates.push([circleLng * RAD_TO_DEG, circleLat * RAD_TO_DEG]);
  }

  return {
    type: 'Feature',
    properties,
    geometry: {
      type: 'Polygon',
      coordinates: [coordinates],
    },
  };
}

export function parseGeometryPolygon(raw: unknown): LatLngTuple[] | null {
  if (!raw) return null;

  if (Array.isArray(raw) && raw.every((position) => isValidLatLng(position))) {
    return stripClosingPosition(raw as LatLngTuple[]);
  }

  let candidate = raw;
  if (typeof candidate === 'object' && candidate !== null) {
    const feature = candidate as { geometry?: unknown };
    if (feature.geometry && typeof feature.geometry === 'object') {
      candidate = feature.geometry;
    }
  }

  if (typeof candidate === 'object' && candidate !== null) {
    const obj = candidate as Record<string, unknown>;
    const coordinates = obj['coordinates'];
    if (Array.isArray(coordinates) && Array.isArray(coordinates[0])) {
      const ring = coordinates[0] as unknown[];
      const positions = ring
        .map((coordinate): LatLngTuple | null => {
          if (!Array.isArray(coordinate) || coordinate.length < 2) return null;
          const [lng, lat] = coordinate as [unknown, unknown];
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
          return [Number(lat), Number(lng)];
        })
        .filter((position): position is LatLngTuple => position !== null);

      return positions.length >= 3 ? stripClosingPosition(positions) : null;
    }
  }

  if (typeof raw === 'string') {
    const wktMatch = raw.match(/POLYGON\s*\(\((.+)\)\)/i);
    if (!wktMatch) return null;

    const positions = wktMatch[1]
      .split(',')
      .map((pair) => pair.trim())
      .map((pair): LatLngTuple | null => {
        const [lng, lat] = pair.split(/\s+/);
        const parsedLng = Number.parseFloat(lng);
        const parsedLat = Number.parseFloat(lat);
        if (!Number.isFinite(parsedLng) || !Number.isFinite(parsedLat)) return null;
        return [parsedLat, parsedLng];
      })
      .filter((position): position is LatLngTuple => position !== null);

    return positions.length >= 3 ? stripClosingPosition(positions) : null;
  }

  return null;
}

export function getPolygonCentroid(positions: LatLngTuple[]): LatLngTuple | null {
  const normalized = stripClosingPosition(positions);
  if (normalized.length < 3) return null;

  let signedArea = 0;
  let centroidLng = 0;
  let centroidLat = 0;

  for (let index = 0; index < normalized.length; index += 1) {
    const [currentLat, currentLng] = normalized[index];
    const [nextLat, nextLng] = normalized[(index + 1) % normalized.length];
    const factor = (currentLng * nextLat) - (nextLng * currentLat);
    signedArea += factor;
    centroidLng += (currentLng + nextLng) * factor;
    centroidLat += (currentLat + nextLat) * factor;
  }

  if (signedArea === 0) {
    const averageLat = normalized.reduce((total, [lat]) => total + lat, 0) / normalized.length;
    const averageLng = normalized.reduce((total, [, lng]) => total + lng, 0) / normalized.length;
    return [averageLat, averageLng];
  }

  const area = signedArea / 2;
  return [
    centroidLat / (6 * area),
    centroidLng / (6 * area),
  ];
}
